import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { useDispatch } from "Hooks/useDispatch";

import {
  Avatar,
  Button,
  ButtonDisclosure,
  Dropdown,
  DropdownMenu,
  IconButton,
  PageHeader,
  Dialog,
} from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import classes from "./AITutorCodeEditorModality.scss";
import RenameModal from "./components/RenameModal/index.js";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import Editor from "@monaco-editor/react";

import { CodeOutlined, PencilOutlined } from "@toddle-edu/ds-icons";
import Loading from "UIComponents/Loading";
import { MARKDOWN } from "Constants/stringConstants";

import { getInitialCodeEditorObj, getFormattedCode } from "Utils/AIUtils";
import UIModal from "UIComponents/UIModal/UIModal";
import { CODE_EDITOR_PROGRAMMING_LANGUAGES } from "@toddle-edu/common-utils";
import { updateAttachment } from "./modules/AITutorCodeEditorModalityModule.js";
import { getAttachmentModalityQuery } from "./modules/AITutorCodeEditorModalityQueries.js";
import { graphql, compose } from "lib/apolloClient";

const codeEditorOptions = mode => {
  return {
    minimap: {
      enabled: false,
    },
    fontSize: 16,
    readOnly: mode === "view",
  };
};

const AITutorCodeEditorModality = props => {
  const {
    inputFormat,
    outputFormat,
    onClose,
    theme = "vs-dark",
    attachment,
    isLoading,
    mode,
  } = props;

  const [fileName, setFileName] = useState("");
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [codeEditorObj, setCodeEditorObj] = useState({});
  const [showDialogInfo, setShowDialogInfo] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  const { t } = useI18n(["common"]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (isLoading) return;

    const initialState = getInitialCodeEditorObj({
      input: attachment?.modalityAttachment?.content,
      inputFormat,
    });

    if (
      !_.isEmpty(attachment) &&
      !_.isEmpty(initialState) &&
      _.isEmpty(codeEditorObj)
    ) {
      setCodeEditorObj(initialState);
    }
  }, [attachment, inputFormat, codeEditorObj, isLoading]);

  useEffect(() => {
    setFileName(attachment?.name || "Untitled");
  }, [attachment]);

  const codeEditorUpdatedOptions = codeEditorOptions(mode);

  const handleCodeChange = updatedCode => {
    setCodeEditorObj(prevState => ({
      ...prevState,
      code: updatedCode,
    }));
  };

  const onDropdownOptionClick = ({ key }) => {
    setCodeEditorObj(prevState => ({
      ...prevState,
      language: key,
    }));
  };

  const toggleDialogInfo = () => {
    setShowDialogInfo(!showDialogInfo);
  };

  const onDone = async () => {
    const content = getFormattedCode({
      codeEditorObj,
      outputFormat,
    });

    setShowFullScreenLoader(true);

    await dispatch(
      updateAttachment({
        id: _.get(attachment, "id"),
        modalityFile: {
          id: _.get(attachment, "modalityAttachment.id"),
          content,
          metadata: { language: codeEditorObj.language },
        },
      })
    );

    setShowFullScreenLoader(false);

    onClose();
  };

  const onRename = name => {
    dispatch(
      updateAttachment({
        id: _.get(attachment, "id"),
        name,
      })
    );
  };

  const toggleRenameModal = () => {
    setShowRenameModal(prev => !prev);
  };

  const openRenameModal = () => {
    setShowRenameModal(true);
  };

  const onRenameEditor = name => {
    onRename(name);
    setFileName(name);
    toggleRenameModal();
  };

  const selectedLanguageLabel = _.get(
    _.find(CODE_EDITOR_PROGRAMMING_LANGUAGES, { key: codeEditorObj.language }),
    "label"
  );

  const editButton = (
    <IconButton
      _legacy_variant="plan"
      variant="neutral"
      type="plain"
      icon={
        <PencilOutlined
          style={{ cursor: "pointer" }}
          size={"xx-small"}
          variant="subtle"
          onClick={openRenameModal}
        />
      }
      size={"small"}
    />
  );

  const renderModalContent = () => {
    return (
      <>
        {mode !== "view" ? (
          <PageHeader dsVersion={"2.0"}>
            <PageHeader.Left
              onBackClick={toggleDialogInfo}
              title={fileName}
              icon={
                <Avatar
                  shape={"square"}
                  shade={"light"}
                  size={"small"}
                  iconType="icon"
                  dsVersion={"2.0"}
                  icon={<CodeOutlined />}
                />
              }
              titleSuffix={editButton}
            />

            <PageHeader.Right>
              <Dropdown
                overlay={
                  <DropdownMenu
                    dsVersion="2.0"
                    options={CODE_EDITOR_PROGRAMMING_LANGUAGES}
                    showSelection={true}
                    value={codeEditorObj.language}
                    onClick={onDropdownOptionClick}
                  />
                }
              >
                <ButtonDisclosure
                  variant="neutral"
                  type="plain"
                  dsVersion="2.0"
                  size="medium"
                >
                  {selectedLanguageLabel}
                </ButtonDisclosure>
              </Dropdown>
              <Button
                variant="progressive"
                dsVersion="2.0"
                size="medium"
                onClick={onDone}
                disabled={showFullScreenLoader}
              >
                {t("common:done")}
              </Button>
            </PageHeader.Right>
          </PageHeader>
        ) : null}

        <Editor
          key={mode}
          value={codeEditorObj.code}
          language={codeEditorObj.language}
          theme={theme}
          loading={<Loading containerStyle={{ height: "100%" }} />}
          onChange={handleCodeChange}
          options={codeEditorUpdatedOptions}
        />

        {showRenameModal ? (
          <RenameModal
            title={t("classRoom:rename_document")}
            closeModal={toggleRenameModal}
            onDocumentNameSubmit={onRenameEditor}
            initialValue={fileName}
          />
        ) : null}
      </>
    );
  };

  if (mode === "view") {
    return (
      <div className={classes.viewContainer}>
        {isLoading ? <Loading /> : renderModalContent()}
      </div>
    );
  }

  return (
    <UIModal isOpen={true} isFullScreen={true}>
      {isLoading ? <Loading /> : renderModalContent()}

      {showDialogInfo ? (
        <Dialog
          dsVersion="2.0"
          variant="delete-dialog"
          title={t("common:unsaved_changes")}
          message={t("common:unsaved_changes_warning")}
          buttonOneText={t("common:keep_editing")}
          buttonTwoText={t("common:save_and_exit")}
          onButtonOneClick={toggleDialogInfo}
          onButtonTwoClick={() => {
            toggleDialogInfo();
            onDone();
          }}
          onClose={toggleDialogInfo}
        />
      ) : null}

      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </UIModal>
  );
};

AITutorCodeEditorModality.defaultProps = {
  onClose: () => {},
  inputFormat: MARKDOWN,
  outputFormat: MARKDOWN,
  mode: "edit",
};

AITutorCodeEditorModality.propTypes = {
  onClose: PropTypes.func,
  inputFormat: PropTypes.string,
  outputFormat: PropTypes.string,
  options: PropTypes.object,
};

export default compose(
  graphql(getAttachmentModalityQuery, {
    name: "getAttachmentModality",
    options: ({ attachmentId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: attachmentId },
    }),
    skip: ({ attachmentId }) => _.isEmpty(attachmentId),
    props: ({ getAttachmentModality: { node: attachment, networkStatus } }) => {
      return {
        attachment,
        isData: !_.isEmpty(attachment),
        isLoading: _.includes([1, 2], networkStatus),
      };
    },
  })
)(AITutorCodeEditorModality);
